import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { GET_PARTNERS } from '../../../GraphQL/partners/queries';

const Wrapper = styled.div`
    margin-bottom: 1rem;
  `;

interface PartnerOption {
  label: string;
  value: number;
}

interface Props {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (field: string, touched: boolean) => void;
  touched: any;
  errors: any;
}

export const PartnerCheckboxes: React.FC<Props> = ({
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
}) => {
  const audience = values.audience;

  const { data, loading, error } = useQuery(GET_PARTNERS, {
    skip: audience !== 'PARTNER',
  });

  // Clear selected partners when switching away from PARTNER audience
  useEffect(() => {
    if (audience !== 'PARTNER' && values.partnershipIds?.length) {
      setFieldValue('partnershipIds', []);
    }
  }, [audience, values.partnershipIds, setFieldValue]);

  // Filter out invalid partners + ensure value is number
  const partnerOptions = useMemo((): PartnerOption[] => {
    if (!data?.partners?.items) return [];

    return data.partners.items
      .filter((p: any) => {
        const id = Number(p.partnershipId);
        return p.status === 'ACCEPTED' && !isNaN(id) && id > 0;
      })
      .map((p: any): PartnerOption => ({
        label: p.companyBName,
        value: Number(p.partnershipId),
      }));
  }, [data]);

  // Only show when audience is PARTNER
  if (audience !== 'PARTNER') return null;

  if (loading) return <Wrapper><p className="text-muted small">Loading partners…</p></Wrapper>;
  if (error) return <Wrapper><div className="alert alert-danger">Error loading partners</div></Wrapper>;
  if (!partnerOptions.length) return <Wrapper><p className="text-muted">No active partners available.</p></Wrapper>;

  const togglePartner = (partnerId: number) => {
    const current = Array.isArray(values.partnershipIds)
      ? values.partnershipIds.map(Number).filter((n: number) => n > 0)
      : [];

    const next = current.includes(partnerId)
      ? current.filter((id: number) => id !== partnerId)
      : [...current, partnerId];

    setFieldValue('partnershipIds', next);
    setFieldTouched('partnershipIds', true);
  };

  return (
    <Wrapper>
      <div>
        <Label>Share with specific partners *</Label>

        <div
          style={{
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid #ced4da',
            borderRadius: '0.375rem',
            padding: '0.5rem',
            backgroundColor: '#fff',
          }}
          className={touched.partnershipIds && errors.partnershipIds ? 'is-invalid' : ''}
        >
          {partnerOptions.map((opt) => {
            const checked = values.partnershipIds
              ?.map(Number)
              .includes(opt.value) ?? false;

            return (
              <div key={opt.value} className="flex items-center space-x-2 mb-2">
                <Checkbox
                  id={`partner-${opt.value}`}
                  checked={checked}
                  onCheckedChange={() => togglePartner(opt.value)}
                  onBlur={() => setFieldTouched('partnershipIds', true)}
                />
                <label
                  htmlFor={`partner-${opt.value}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {opt.label}
                </label>
              </div>
            );
          })}
        </div>

        {touched.partnershipIds && errors.partnershipIds && (
          <div className="text-sm text-red-600 mt-1">{errors.partnershipIds}</div>
        )}

        {/* Hidden input to satisfy Formik */}
        <textarea
          name="partnershipIds"
          value={values.partnershipIds?.join(',') ?? ''}
          readOnly
          style={{ display: 'none' }}
        />
      </div>
    </Wrapper>
  );
};